import { useState, useRef } from "react";
import { Send, Paperclip, Mic, Sliders, Square } from "lucide-react";
import { FileAttachment } from "../../types";
import { MCPSettings } from "../settings/MCPSettings";

interface ChatInputProps {
  onSendMessage: (message: string, attachments?: FileAttachment[]) => void;
  isTyping?: boolean;
  disabled?: boolean;
  onOpenSettings?: () => void;
  mcpServices?: Record<string, boolean>;
  onUpdateMcpServices?: (services: Record<string, boolean>) => void;
  onStop?: () => void;
}

export function ChatInput({ 
  onSendMessage, 
  isTyping = false, 
  disabled = false, 
  onOpenSettings,
  mcpServices = {},
  onUpdateMcpServices,
  onStop
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [showMcpSettings, setShowMcpSettings] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const handleSend = () => {
    if (value.trim() || attachments.length > 0) {
      onSendMessage(value, attachments);
      setValue("");
      setAttachments([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newAttachments: FileAttachment[] = [];

    for (const file of files) {
      // Increased file size limit to 25MB for documents
      if (file.size > 25 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 25MB.`);
        continue;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      const data = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
      });

      newAttachments.push({
        id: Date.now().toString(),
        name: file.name,
        data,
        type: file.type,
        size: file.size
      });
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {attachments.map((att) => (
              <div key={att.id} className="bg-slate-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                <span className="text-white">{att.name}</span>
                <button 
                  onClick={() => removeAttachment(att.id)} 
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <span>×</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Container */}
      <div 
        className="bg-slate-800 px-6 py-4 flex items-center gap-4"
        style={{
          borderRadius: '24px 24px 24px 24px',
          boxShadow: `
            0 0 0 1px rgba(255, 255, 255, 0.05),
            0 1px 3px rgba(0, 0, 0, 0.4),
            0 4px 12px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `
        }}
      >
        {/* Input field */}
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={isTyping ? "AI is thinking..." : "Ask anything - AI will automatically route to Notion, email, or chat..."}
          className="flex-1 bg-transparent border-none outline-none resize-none text-white text-base placeholder-slate-400 leading-relaxed"
          rows={1}
          disabled={disabled}
          style={{ minHeight: '24px' }}
        />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button 
            onClick={handleFileAttach}
            className="text-slate-400 hover:text-white p-2 transition-colors"
            disabled={disabled || isTyping}
            title="Upload files: PDF, Word, Excel, PowerPoint, Text, CSV, Images, and more"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.html,.htm,.xml,.rtf,image/*"
            className="hidden"
          />

          {/* Mic button */}
          <button
            title="Voice input (coming soon)"
            className="text-slate-400 hover:text-white p-2 transition-colors"
            disabled={disabled || isTyping}
          >
            <Mic className="w-5 h-5" />
          </button>

          <div className="relative">
            <button 
              ref={settingsButtonRef}
              onClick={() => setShowMcpSettings(!showMcpSettings)}
              className="text-slate-400 hover:text-white p-2 transition-colors"
              disabled={disabled || isTyping}
            >
              <Sliders className="w-5 h-5" />
            </button>
            {showMcpSettings && onUpdateMcpServices && (
              <MCPSettings
                mcpServices={mcpServices}
                onUpdateServices={onUpdateMcpServices}
                isOpen={showMcpSettings}
                onClose={() => setShowMcpSettings(false)}
              />
            )}
          </div>

          {isTyping ? (
            <button
              onClick={() => onStop?.()}
              className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-full transition-colors"
              disabled={disabled}
              title="Stop generation"
            >
              <Square className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded-full transition-colors"
              disabled={disabled || (!value.trim() && attachments.length === 0)}
            >
              <Send className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 